/**
 * Legal API Service - Frontend App
 * Il Diario di Bordo - SaaS
 * 
 * Service per gestire documenti legali lato utente:
 * - Ottenere documenti pendenti
 * - Firmare/accettare documenti
 * - Storico firme
 * - Visualizzare documenti firmati
 * 
 * @version 1.1.0
 * @updated Febbraio 2025
 */

import api from './api'

export const legalApi = {
  
  // ============================================
  // DOCUMENTI PUBBLICI
  // ============================================
  
  /**
   * Lista documenti legali attivi (pubblico)
   * Usato per footer, link, ecc.
   */
  getPublicDocuments: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/legal/documents${query ? `?${query}` : ''}`)
  },
  
  /**
   * Ottieni documento pubblico per tipo
   * Usato dalla landing page
   */
  getDocumentByType: (type) => {
    return api.get(`/legal/documents/type/${type}`)
  },
  
  /**
   * Ottieni documento pubblico per ID
   */
  getDocument: (id) => {
    return api.get(`/legal/documents/${id}`)
  },
  
  // ============================================
  // DOCUMENTI PENDENTI (AUTENTICATO)
  // ============================================
  
  /**
   * Ottieni documenti pendenti per l'utente corrente
   * Ritorna documenti divisi per tipo di blocco
   */
  getPendingDocuments: () => {
    return api.get('/legal/pending')
  },
  
  /**
   * Verifica se ci sono documenti bloccanti
   * Utile per check rapido senza caricare tutti i dati
   */
  hasBlockingDocuments: async () => {
    const response = await api.get('/legal/pending')
    return response.data.data.hasBlockingDocuments
  },
  
  // ============================================
  // FIRMA DOCUMENTI
  // ============================================
  
  /**
   * Firma/accetta un documento
   * @param {string} documentId - ID del documento
   * @param {object} data - Dati firma
   * @param {boolean} data.accepted - Accettato
   * @param {boolean} data.fullReadCompleted - Scroll completo
   * @param {number} data.timeSpentSeconds - Tempo speso
   */
  acknowledgeDocument: (documentId, data = {}) => {
    return api.post(`/legal/acknowledge/${documentId}`, {
      accepted: true,
      ...data
    })
  },
  
  /**
   * Alias per acknowledgeDocument
   */
  signDocument: (documentId, data = {}) => {
    return legalApi.acknowledgeDocument(documentId, data)
  },
  
  // ============================================
  // STORICO FIRME
  // ============================================
  
  /**
   * Ottieni storico firme dell'utente corrente
   */
  getMyAcknowledgments: () => {
    return api.get('/legal/acknowledgments')
  },
  
  /**
   * Alias per getMyAcknowledgments
   */
  getSignatureHistory: () => {
    return legalApi.getMyAcknowledgments()
  },
  
  // ============================================
  // VISUALIZZAZIONE DOCUMENTI FIRMATI
  // ============================================
  
  /**
   * Ottieni il contenuto completo di un documento firmato
   * Usato per visualizzare cosa l'utente ha accettato
   * Funziona anche per documenti archiviati
   * 
   * @param {string} acknowledgmentId - ID della firma (acknowledgment)
   * @returns {Promise} - { document, signature }
   */
  getSignedDocumentContent: (acknowledgmentId) => {
    return api.get(`/legal/acknowledgments/${acknowledgmentId}/document`)
  },
  
  /**
   * Alias per getSignedDocumentContent
   */
  viewSignedDocument: (acknowledgmentId) => {
    return legalApi.getSignedDocumentContent(acknowledgmentId)
  }
}

export default legalApi
